import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { makeStyles } from '@material-ui/core/styles';
import {
	Container,
	CssBaseline,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@material-ui/core';
import { useGetAdminBookArchiveMonitorQuery } from '../../../store/apiSlice';
import Const from '../../../defs/const';

const STATUS_LABELS = {
	[Const.BOOK_ARCHIVE_STATUS_PENDING]: 'Чака обработка',
	[Const.BOOK_ARCHIVE_STATUS_DOWNLOADING_FILES]: 'Сваля файловете',
	[Const.BOOK_ARCHIVE_STATUS_CREATING_ARCHIVE]: 'Създава ZIP архива',
	[Const.BOOK_ARCHIVE_STATUS_UPLOADING_ARCHIVE]: 'Качва архива в облака',
	[Const.BOOK_ARCHIVE_STATUS_CREATED]: 'Готов',
	[Const.BOOK_ARCHIVE_STATUS_DELETING]: 'Изтрива архива',
	[Const.BOOK_ARCHIVE_STATUS_FAILED]: 'Неуспешно',
};

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
	},
	title: {
		paddingBottom: theme.spacing(2),
	},
	statusBox: {
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2),
	},
	table: {
		minWidth: 650,
	},
	secondaryText: {
		marginTop: theme.spacing(1),
	},
}));

function formatDate(value) {
	if (!value) return 'Няма';

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;

	return parsed.toLocaleString('bg-BG');
}

function formatStatus(status) {
	if (!status) return 'Няма';

	return `${STATUS_LABELS[status] || status} (${status})`;
}

function formatProgress(progress) {
	return typeof progress === 'number' ? `${progress}%` : 'Няма';
}

function buildSummary(currentArchive, archives) {
	if (currentArchive) {
		const title = currentArchive.book?.title || 'без заглавие';
		return `В момента се обработва архивът на „${title}“, книга номер ${currentArchive.book_id}. Локален статус: ${formatStatus(currentArchive.status)}. Cloud статус: ${formatStatus(currentArchive.odArchive?.status)}. Прогрес: ${formatProgress(currentArchive.odArchive?.progress)}.`;
	}

	if (archives.length) {
		return `В момента няма активна обработка. Има ${archives.length} неприключени архива, които чакат повторен опит.`;
	}

	return 'Няма неприключени архиви.';
}

export function ArchiveMonitor() {
	const classes = useStyles();
	const { data, isFetching, isError } = useGetAdminBookArchiveMonitorQuery(
		undefined,
		{
			pollingInterval: 10000,
			refetchOnFocus: true,
			refetchOnReconnect: true,
		}
	);

	const archives = Array.isArray(data?.archives) ? data.archives : [];
	const currentArchive = data?.currentArchive || null;
	const summary = buildSummary(currentArchive, archives);

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Helmet>
				<title>Архивиране</title>
			</Helmet>

			<Container maxWidth="lg" className={classes.container}>
				<Paper className={classes.paper}>
					<Typography
						className={classes.title}
						component="h1"
						variant="h4"
						role="alert"
					>
						Архивиране
					</Typography>

					<Paper
						variant="outlined"
						className={classes.statusBox}
						role="status"
						aria-live="polite"
					>
						<Typography variant="body1">{summary}</Typography>
						<Typography
							variant="body2"
							color="textSecondary"
							className={classes.secondaryText}
						>
							Последно обновяване от сървъра: {formatDate(data?.fetchedAt)}
						</Typography>
					</Paper>

					{isError ? (
						<Typography color="error">
							Неуспешно зареждане на статуса на архивите.
						</Typography>
					) : null}

					{!archives.length && !isFetching ? (
						<Typography>Няма архиви за наблюдение.</Typography>
					) : (
						<TableContainer component={Paper}>
							<Table className={classes.table} aria-label="Статус на архивите">
								<TableHead>
									<TableRow>
										<TableCell>Архив</TableCell>
										<TableCell>Книга</TableCell>
										<TableCell>Локален статус</TableCell>
										<TableCell>Cloud статус</TableCell>
										<TableCell>Прогрес</TableCell>
										<TableCell>Последно обновяване</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{archives.map((archive) => (
										<TableRow key={archive.id}>
											<TableCell scope="row">
												<Typography variant="body2">
													#{archive.id}
												</Typography>
												<Typography variant="body2" color="textSecondary">
													{archive.name || archive.file_name || 'archive.zip'}
												</Typography>
											</TableCell>
											<TableCell>
												{archive.book?.id ? (
													<Link to={`/admin/catalogue/edit/${archive.book.id}`}>
														{archive.book.title || `Книга #${archive.book.id}`}
													</Link>
												) : (
													'Липсваща книга'
												)}
												{archive.book?.id ? (
													<Typography variant="body2" color="textSecondary">
														Книга №{archive.book.id}
													</Typography>
												) : null}
											</TableCell>
											<TableCell>{formatStatus(archive.status)}</TableCell>
											<TableCell>{formatStatus(archive.odArchive?.status)}</TableCell>
											<TableCell>{formatProgress(archive.odArchive?.progress)}</TableCell>
											<TableCell>{formatDate(archive.latest_updated_at)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			</Container>
		</div>
	);
}
